import { z } from 'zod';
import {
  CAMPAIGN_STATUSES,
  CONTACT_STATUSES,
  CONTENT_STATUSES,
  PROJECT_STAGES,
  SOCIAL_PLATFORMS,
  SUBSCRIBER_STATUSES,
} from './constants';
import { translationsSchema } from './i18n';

// ───────────── helpers ─────────────

const trimmed = (max: number) => z.string().trim().max(max);
const requiredText = (max: number, label = 'This field') =>
  z.string().trim().min(1, `${label} is required`).max(max, `Maximum ${max} characters`);

/** Optional string: empty strings become null so the database stores NULL instead of "". */
export const optionalText = (max: number) =>
  z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .pipe(z.string().max(max, `Maximum ${max} characters`).nullable().optional())
    .transform((v) => (v === '' || v === undefined ? null : v));

/**
 * A link that is safe to render in an href: http(s), mailto:, tel:, a site-relative path or an #anchor.
 * Rejects javascript:, data:, vbscript: and other schemes.
 */
// Site paths may not contain backslashes: browsers read `/\evil.com` as `//evil.com` (off-site).
export const safeLinkRegex =
  /^(https?:\/\/[^\s]+|mailto:[^\s]+|tel:[+\d\s()-]+|\/(?![/\\])[^\s\\]*|#[\w-]*)$/i;
export const linkSchema = z
  .string()
  .trim()
  .max(500)
  .regex(safeLinkRegex, 'Use a full URL (https://…), mailto:, tel:, a /path or a #section');
export const optionalLink = z
  .union([z.literal(''), z.null(), z.undefined(), linkSchema])
  .transform((v) => (v ? v : null));

export const httpUrl = z
  .string()
  .trim()
  .max(500)
  .regex(/^https?:\/\/[^\s]+$/i, 'Use a full URL starting with https://');
export const optionalHttpUrl = z
  .union([z.literal(''), z.null(), z.undefined(), httpUrl])
  .transform((v) => (v ? v : null));

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .max(191)
  .email('Enter a valid email address')
  .transform((v) => v.toLowerCase());

export const slugSchema = z
  .string()
  .trim()
  .min(1, 'Slug is required')
  .max(160)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens');

export const hexColor = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #FA9A21');

const idSchema = z.string().min(1).max(64);
const optionalId = z
  .union([idSchema, z.literal(''), z.null(), z.undefined()])
  .transform((v) => (v ? v : null));

export const slugify = (input: string) =>
  input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 160);

// ───────────── auth ─────────────

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(200)
  .refine((v) => /[a-zA-Z]/.test(v) && /\d/.test(v), 'Use letters and numbers');

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'This reset link is invalid').max(200),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ───────────── common ─────────────

export const contentStatusSchema = z.enum(CONTENT_STATUSES);

export const reorderSchema = z.object({
  ids: z.array(idSchema).min(1).max(1000),
});
export type ReorderInput = z.infer<typeof reorderSchema>;

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  q: z.string().trim().max(120).optional(),
});

// ───────────── projects ─────────────

export const projectImageInputSchema = z.object({
  mediaId: idSchema,
  caption: optionalText(255),
  translations: translationsSchema('projectImage').optional(),
});

export const projectInputSchema = z.object({
  title: requiredText(160, 'Title'),
  slug: slugSchema,
  description: trimmed(20000).default(''),
  categoryId: optionalId,
  client: optionalText(120),
  year: z
    .union([z.coerce.number().int().min(1990).max(2100), z.literal(''), z.null(), z.undefined()])
    .transform((v) => (typeof v === 'number' ? v : null)),
  period: optionalText(40),
  projectUrl: optionalHttpUrl,
  disciplines: z.array(requiredText(40)).max(20).default([]),
  accentColor: z
    .union([hexColor, z.literal(''), z.null(), z.undefined()])
    .transform((v) => (v ? v : null)),
  headline: optionalText(160),
  scope: optionalText(160),
  stage: z
    .union([z.enum(PROJECT_STAGES), z.literal(''), z.null(), z.undefined()])
    .transform((v) => (v ? v : null)),
  storyTitle: optionalText(160),
  storyBody: optionalText(20000),
  storyTitleAr: optionalText(160),
  storyBodyAr: optionalText(20000),
  status: contentStatusSchema.default('DRAFT'),
  featured: z.boolean().default(false),
  coverImageId: optionalId,
  images: z.array(projectImageInputSchema).max(40).default([]),
  translations: translationsSchema('project').optional(),
});
export type ProjectInput = z.input<typeof projectInputSchema>;
export type ProjectInputParsed = z.output<typeof projectInputSchema>;

export const projectPatchSchema = projectInputSchema.partial();

export const projectCategoryInputSchema = z.object({
  name: requiredText(80, 'Name'),
  isPublished: z.boolean().default(true),
  translations: translationsSchema('projectCategory').optional(),
});
export type ProjectCategoryInput = z.input<typeof projectCategoryInputSchema>;

export const projectStatusSchema = z.object({ status: contentStatusSchema });
export const featuredSchema = z.object({ featured: z.boolean() });

export const HOME_PROJECTS_MAX = 24;
/** Homepage Work grid: how many projects to show, and which ones in what order. */
export const homepageProjectsSchema = z.object({
  count: z.number().int().min(1).max(HOME_PROJECTS_MAX),
  ids: z.array(idSchema).max(100),
});
export type HomepageProjectsInput = z.infer<typeof homepageProjectsSchema>;

export const projectListQuery = paginationQuery.extend({
  status: contentStatusSchema.optional(),
  featured: z.enum(['true', 'false']).optional(),
  categoryId: idSchema.optional(),
  sort: z.enum(['order', 'updated', 'title', 'year']).default('order'),
});

// ───────────── testimonials ─────────────

export const testimonialInputSchema = z.object({
  clientName: requiredText(120, 'Client name'),
  clientRole: optionalText(120),
  company: optionalText(120),
  quote: requiredText(3000, 'Testimonial text'),
  avatarId: optionalId,
  logoId: optionalId,
  projectId: optionalId,
  status: contentStatusSchema.default('DRAFT'),
  translations: translationsSchema('testimonial').optional(),
});
export type TestimonialInput = z.input<typeof testimonialInputSchema>;
export const testimonialPatchSchema = testimonialInputSchema.partial();

// ───────────── services / process / clients / socials ─────────────

export const serviceInputSchema = z.object({
  title: requiredText(120, 'Title'),
  description: optionalText(2000),
  icon: optionalText(60),
  isPublished: z.boolean().default(true),
  translations: translationsSchema('service').optional(),
});
export type ServiceInput = z.input<typeof serviceInputSchema>;

export const processStepInputSchema = z.object({
  title: requiredText(120, 'Title'),
  description: requiredText(2000, 'Description'),
  isPublished: z.boolean().default(true),
  translations: translationsSchema('processStep').optional(),
});
export type ProcessStepInput = z.input<typeof processStepInputSchema>;

export const faqItemInputSchema = z.object({
  question: requiredText(200, 'Question'),
  answer: requiredText(2000, 'Answer'),
  isPublished: z.boolean().default(true),
  translations: translationsSchema('faqItem').optional(),
});
export type FaqItemInput = z.input<typeof faqItemInputSchema>;

export const clientBrandInputSchema = z.object({
  name: requiredText(120, 'Name'),
  url: optionalHttpUrl,
  logoId: optionalId,
  highlighted: z.boolean().default(false),
  storyTitle: optionalText(120),
  storyText: optionalText(500),
  storyLabel: optionalText(120),
  storyImageId: optionalId,
  storyImageMobileId: optionalId,
  storyImageArId: optionalId,
  storyImageMobileArId: optionalId,
  isPublished: z.boolean().default(true),
  translations: translationsSchema('clientBrand').optional(),
});
export type ClientBrandInput = z.input<typeof clientBrandInputSchema>;

export const socialLinkInputSchema = z.object({
  platform: z.enum(SOCIAL_PLATFORMS),
  label: requiredText(120, 'Label'),
  url: linkSchema,
  isPublished: z.boolean().default(true),
});
export type SocialLinkInput = z.input<typeof socialLinkInputSchema>;

// ───────────── statistics ─────────────

export const statisticItemSchema = z.object({
  id: idSchema,
  label: requiredText(80, 'Label'),
  value: z.coerce
    .number({ invalid_type_error: 'Enter a number' })
    .int('Use a whole number')
    .min(0, 'Must be 0 or more')
    .max(1_000_000_000),
  prefix: optionalText(8),
  suffix: optionalText(8),
  isVisible: z.boolean(),
  translations: translationsSchema('statistic').optional(),
});
export const statisticsUpdateSchema = z.object({
  items: z.array(statisticItemSchema).min(1).max(12),
});
export type StatisticsUpdateInput = z.input<typeof statisticsUpdateSchema>;

// ───────────── site settings ─────────────

export const siteSettingsSchema = z.object({
  siteTitle: requiredText(120, 'Site title'),
  metaDescription: requiredText(300, 'Meta description'),
  ogImageId: optionalId,
  faviconId: optionalId,

  navAboutLabel: requiredText(40),
  navWorkLabel: requiredText(40),
  navServicesLabel: requiredText(40),
  navInsightsLabel: requiredText(40),
  navCtaLabel: requiredText(40),
  navCtaUrl: linkSchema,

  heroLightText: requiredText(200, 'Headline'),
  heroBoldText: requiredText(120, 'Bold line'),
  heroHighlightText: requiredText(120, 'Highlighted word'),
  heroPortraitId: optionalId,
  heroShowPortrait: z.boolean(),
  heroPrimaryLabel: requiredText(60),
  heroPrimaryUrl: linkSchema,
  heroSecondaryLabel: optionalText(60),
  heroSecondaryUrl: optionalLink,

  ownerName: requiredText(120, 'Name'),
  ownerTagline: optionalText(120),
  roles: requiredText(300, 'Roles'),
  education: optionalText(300),
  bioPrimary: requiredText(3000, 'Biography'),
  bioSecondary: optionalText(3000),
  location: optionalText(120),
  contactEmail: z
    .union([z.literal(''), z.null(), z.undefined(), emailSchema])
    .transform((v) => (v ? v : null)),
  contactPhone: optionalText(40),
  whatsappNumber: z
    .union([
      z.literal(''),
      z.null(),
      z.undefined(),
      z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,24}$/, 'Use the international format, e.g. +213 781 60 68 17'),
    ])
    .transform((v) => (v ? v : null)),
  clientsHeading: requiredText(200),

  bannerEnabled: z.boolean(),

  workHeading: requiredText(160),
  workIntro: optionalText(400),
  workNote: optionalText(200),
  workNoteDetail: optionalText(300),
  servicesHeading: requiredText(160),
  servicesIntro: optionalText(400),
  processHeading: requiredText(160),
  processIntro: optionalText(400),
  processNote: optionalText(200),
  faqHeading: requiredText(160),
  testimonialsHeading: requiredText(160),
  storyCtaHeading: requiredText(160),
  storyCtaText: optionalText(400),
  storyCtaLabel: requiredText(60),
  storyCtaUrl: linkSchema,

  contactHeading: requiredText(160),
  contactIntro: optionalText(500),
  contactCtaLabel: requiredText(60),
  newsletterEnabled: z.boolean(),
  newsletterHeading: requiredText(160),
  newsletterIntro: optionalText(400),
  footerText: optionalText(300),
  translations: translationsSchema('siteSettings').optional(),
});
export type SiteSettingsInput = z.input<typeof siteSettingsSchema>;
export const siteSettingsPatchSchema = siteSettingsSchema.partial();

// ───────────── contact ─────────────

export const contactInputSchema = z.object({
  name: requiredText(120, 'Name'),
  email: emailSchema,
  company: optionalText(120),
  budget: optionalText(60),
  message: z
    .string()
    .trim()
    .min(10, 'Tell me a little more (10 characters minimum)')
    .max(5000, 'Maximum 5000 characters'),
  /** Honeypot – must stay empty. */
  website: z.string().max(0, 'Spam detected').optional().default(''),
  /** ms timestamp when the form was rendered (bots submit instantly). */
  startedAt: z.coerce.number().int().optional(),
});
export type ContactInput = z.input<typeof contactInputSchema>;

export const contactStatusPatchSchema = z.object({ status: z.enum(CONTACT_STATUSES) });

// ───────────── newsletter ─────────────

export const newsletterSubscribeSchema = z.object({
  email: emailSchema,
  name: optionalText(120),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Please confirm you want to receive the newsletter' }),
  }),
  website: z.string().max(0).optional().default(''),
});
export type NewsletterSubscribeInput = z.input<typeof newsletterSubscribeSchema>;

export const tokenSchema = z.object({ token: z.string().trim().min(16).max(128) });

export const subscriberCreateSchema = z
  .object({
    email: emailSchema,
    name: optionalText(120),
    /** "confirm" sends a double opt-in email; "attested" records that the admin holds documented consent. */
    mode: z.enum(['confirm', 'attested']),
    consentNote: optionalText(255),
  })
  .refine((d) => d.mode !== 'attested' || !!d.consentNote, {
    message: 'Describe where and when this person gave consent',
    path: ['consentNote'],
  });
export type SubscriberCreateInput = z.input<typeof subscriberCreateSchema>;

export const subscriberPatchSchema = z.object({
  name: optionalText(120),
  status: z.enum(['UNSUBSCRIBED']).optional(),
});

export const subscriberBulkSchema = z.object({
  ids: z.array(idSchema).min(1).max(5000),
  action: z.enum(['unsubscribe', 'delete', 'resend-confirmation']),
});

export const subscriberImportOptionsSchema = z
  .object({
    mode: z.enum(['confirm', 'attested']),
    consentNote: optionalText(255),
  })
  .refine((d) => d.mode !== 'attested' || !!d.consentNote, {
    message: 'Describe where and when these contacts gave consent',
    path: ['consentNote'],
  });

export const subscriberListQuery = paginationQuery.extend({
  status: z.enum(SUBSCRIBER_STATUSES).optional(),
  source: z.enum(['WEBSITE', 'ADMIN', 'IMPORT']).optional(),
});

export const templateInputSchema = z.object({
  name: requiredText(120, 'Name'),
  description: optionalText(300),
  subject: optionalText(200),
  previewText: optionalText(200),
  contentHtml: z.string().max(2_000_000),
  contentJson: z.unknown().optional(),
  isDefault: z.boolean().default(false),
});
export type TemplateInput = z.input<typeof templateInputSchema>;

export const campaignInputSchema = z.object({
  name: requiredText(160, 'Internal name'),
  subject: trimmed(200).default(''),
  previewText: optionalText(200),
  fromName: optionalText(120),
  replyTo: z
    .union([z.literal(''), z.null(), z.undefined(), emailSchema])
    .transform((v) => (v ? v : null)),
  contentHtml: z.string().max(2_000_000).default(''),
  contentJson: z.unknown().optional(),
  templateId: optionalId,
  attachmentIds: z.array(idSchema).max(10).default([]),
});
export type CampaignInput = z.input<typeof campaignInputSchema>;
export const campaignPatchSchema = campaignInputSchema.partial();

export const campaignTestSchema = z.object({
  emails: z.array(emailSchema).min(1, 'Add at least one address').max(5),
});

export const campaignScheduleSchema = z.object({
  /** ISO-8601 instant (UTC) */
  scheduledAt: z.string().datetime({ offset: true }),
});

export const campaignSendSchema = z.object({
  /** The number of recipients the admin saw in the confirmation dialog. */
  expectedRecipients: z.number().int().min(0),
  confirm: z.literal('SEND'),
});

export const campaignListQuery = paginationQuery.extend({
  status: z.enum(CAMPAIGN_STATUSES).optional(),
  archived: z.enum(['true', 'false']).default('false'),
});
